import { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { Check, Languages, Loader2, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { useApi } from "../../customer/hooks";
import { PageHead, btnPrimary, chip, input } from "../../../components/common/Page";
import { SkeletonBlock } from "../../../components/common/EmptyState";

const PAGE = 50;

// One product row: Greek name, editable English name, AI proposal (checked = will be saved)
const Row = ({ r, draft, setDraft, proposal, picked, togglePick, onSave }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const save = async () => { setBusy(true); try { await onSave(r.code, draft ?? r.name_en); } finally { setBusy(false); } };
  return (
    <li className="space-y-1.5 px-4 py-3" data-testid={`tr-row-${r.code}`}>
      <p className="text-sm font-semibold text-navy">{r.name_el} <span className="text-xs font-normal text-slate-500">{r.code}{r.brand ? ` · ${r.brand}` : ""}</span></p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input value={draft ?? r.name_en ?? ""} onChange={(e) => setDraft(r.code, e.target.value)} placeholder={t("x.tr.enPh")} className={`${input} h-10 flex-1`} data-testid={`tr-input-${r.code}`} />
        <button type="button" disabled={busy || !(draft ?? r.name_en)} onClick={save} className={`${btnPrimary} h-10 px-3`} data-testid={`tr-save-${r.code}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{t("common.save")}</button>
      </div>
      {proposal !== undefined && (
        <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid={`tr-proposal-${r.code}`}>
          <input type="checkbox" checked={picked} onChange={togglePick} className="h-4 w-4 accent-navy" data-testid={`tr-pick-${r.code}`} />
          <Sparkles className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1 truncate">{proposal || t("x.tr.noProposal")}</span>
        </label>
      )}
    </li>
  );
};

export default function StaffTranslations() {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [missing, setMissing] = useState(true);
  const [offset, setOffset] = useState(0);
  const [drafts, setDrafts] = useState({});
  const [proposals, setProposals] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const list = useApi("translations", "/admin/catalogue/translations", { missing, q: q || undefined, offset, limit: PAGE }, { staleTime: 0, placeholderData: keepPreviousData });
  const rows = list.data?.items || [];
  const setDraft = (code, v) => setDrafts((d) => ({ ...d, [code]: v }));
  const saveOne = async (code, name_en) => {
    try { await api.put(`/admin/catalogue/items/${encodeURIComponent(code)}/name-en`, { name_en }); toast.success(t("common.saved")); list.refetch(); }
    catch { toast.error(t("common.error")); }
  };
  const translate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/catalogue/translate", { codes: rows.map((r) => r.code) });
      const map = Object.fromEntries(data.proposals.map((p) => [p.code, p.name_en]));
      setProposals(map);
      setPicked(Object.fromEntries(data.proposals.filter((p) => p.name_en).map((p) => [p.code, true])));
    } catch { toast.error(t("common.error")); } finally { setBusy(false); }
  };
  const saveProposals = async () => {
    const items = Object.entries(picked).filter(([, v]) => v).map(([code]) => ({ code, name_en: drafts[code] || proposals[code], ai: true })).filter((x) => x.name_en);
    if (!items.length) return;
    setBusy(true);
    try { const { data } = await api.put("/admin/catalogue/translations/bulk", { items }); toast.success(t("x.tr.savedN", { n: data.saved })); setProposals(null); setPicked({}); setDrafts({}); list.refetch(); }
    catch { toast.error(t("common.error")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="staff-translations-page">
      <PageHead title={t("x.tr.title")} sub={t("x.tr.sub", { n: list.data?.missing_total ?? "…" })} />
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative block w-full max-w-md"><Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" /><input value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} placeholder={t("shop.searchPh")} className={`${input} pl-9`} data-testid="tr-search" /></label>
        <button type="button" className={chip(missing)} onClick={() => { setMissing(true); setOffset(0); }} data-testid="tr-filter-missing">{t("x.tr.onlyMissing")}</button>
        <button type="button" className={chip(!missing)} onClick={() => { setMissing(false); setOffset(0); }} data-testid="tr-filter-all">{t("cstat.all")}</button>
        <span className="flex-1" />
        {proposals ? (
          <button type="button" disabled={busy} onClick={saveProposals} className={`${btnPrimary} h-10 px-4`} data-testid="tr-save-proposals">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{t("x.tr.saveProposals", { n: Object.values(picked).filter(Boolean).length })}</button>
        ) : (
          <button type="button" disabled={busy || !rows.length} onClick={translate} className={`${btnPrimary} h-10 px-4`} data-testid="tr-ai-translate">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}{t("x.tr.aiTranslate", { n: rows.length })}</button>
        )}
      </div>
      {proposals && <p className="rounded-xl bg-amber-50 px-4 py-2 text-xs text-amber-900" data-testid="tr-review-note">{t("x.tr.reviewNote")}</p>}
      {list.isLoading ? <SkeletonBlock className="h-64" /> : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm" data-testid="tr-list">
          {rows.map((r) => <Row key={r.code} r={r} draft={drafts[r.code]} setDraft={setDraft} proposal={proposals ? proposals[r.code] : undefined}
            picked={!!picked[r.code]} togglePick={() => setPicked((p) => ({ ...p, [r.code]: !p[r.code] }))} onSave={saveOne} />)}
          {!rows.length && <li className="px-4 py-6 text-sm text-slate-500" data-testid="tr-empty">{t("x.tr.empty")}</li>}
        </ul>
      )}
      <div className="flex items-center justify-between text-sm text-slate-600">
        <button type="button" disabled={!offset} onClick={() => { setOffset((o) => Math.max(0, o - PAGE)); setProposals(null); }} className="h-10 rounded-full bg-white px-4 font-semibold text-navy shadow-sm disabled:opacity-40" data-testid="tr-prev">‹</button>
        <span data-testid="tr-page-info">{offset + 1}–{Math.min(offset + PAGE, list.data?.total || 0)} / {list.data?.total ?? 0}</span>
        <button type="button" disabled={offset + PAGE >= (list.data?.total || 0)} onClick={() => { setOffset((o) => o + PAGE); setProposals(null); }} className="h-10 rounded-full bg-white px-4 font-semibold text-navy shadow-sm disabled:opacity-40" data-testid="tr-next">›</button>
      </div>
    </div>
  );
}
